/**
 * /billing/family-balance — Family balance variant (Plan 09).
 *
 * Surfaces the parent-facing wallet UI:
 *
 *   1. Balance card     — current wallet balance + auto-reload toggle
 *                         + "Top up" CTA.
 *   2. Payment methods  — list + add affordance.
 *   3. Billing history  — paginated ledger with kind-filter chips +
 *                         CSV export.
 *
 * The page is rendered inline from the parent BillingPage; it
 * receives no props and reads the user's HOUSEHOLD Account directly
 * via `BillingFamilyQuery`. The branching switcher + page title live
 * on the parent.
 *
 * Loading state is the block-for-block
 * `<BillingFamilyBalanceSkeleton />`. Apollo error surfaces as a
 * sonner toast with a retry button (mirrors the /my-family pattern).
 *
 * The wallet-sharing toggle is NOT surfaced in v1 — see
 * `wallet-sharing-toggle-sheet.tsx` for the release-coupling
 * rationale.
 */

import { toast } from '@ordo/ui/sonner'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router'

import { useBillingFamilyQuery } from '@/api/billing-family'
import { BalanceCard } from '@/components/billing/balance-card'
import { findActiveHousehold } from '@/components/billing/helpers'
import { LedgerTable } from '@/components/billing/ledger-table'
import { PaymentMethodsCard } from '@/components/billing/payment-methods-card'
import { ReturnToOrderBar } from '@/components/billing/return-to-order-bar'
import { TopUpSheet } from '@/components/billing/top-up-sheet'
import { useTopUpWatch } from '@/components/billing/use-top-up-watch'
import { NoHouseholdState } from '@/components/family/no-household-state'
import { RETURN_TO_PARAM, safeReturnTo } from '@/lib/return-to'
import { BillingFamilyBalanceSkeleton } from '@/skeletons/billing-family-balance-skeleton'

/** Query flag that opens the add-payment-method flow on arrival. */
const ADD_PAYMENT_METHOD_PARAM = 'addPaymentMethod'

export function FamilyBalancePage() {
  const { data, isPending, error, refetch } = useBillingFamilyQuery({ first: 200 })

  const [topUpOpen, setTopUpOpen] = useState(false)
  // A co-parent may fund a household they don't own (TOP_UP), so the top-up
  // sheet has to be addressable per shared household, not just the owned one.
  const [sharedTopUpAccountId, setSharedTopUpAccountId] = useState<string | null>(null)
  const topUp = useTopUpWatch()

  // launch-gap 09 — the "back to what you were doing" loop. Order
  // surfaces link here with ?returnTo=<in-app path>; the allow-list
  // rejects anything that could leave the app. `landed` flips once the
  // top-up watcher confirms the credit arrived (Stripe path); test
  // top-ups credit synchronously and simply keep the neutral copy.
  const [searchParams, setSearchParams] = useSearchParams()
  const returnTo = safeReturnTo(searchParams.get(RETURN_TO_PARAM))
  // The payment-failure email's button (server `ADD_PAYMENT_METHOD_DEEP_LINK`).
  const openAddPaymentMethod = searchParams.get(ADD_PAYMENT_METHOD_PARAM) === '1'
  const [watchStarted, setWatchStarted] = useState(false)
  const topUpLanded = watchStarted && topUp.pendingAccountId === null && !topUp.stillProcessing

  useEffect(() => {
    if (error) {
      toast.error("Couldn't load your balance", {
        description: error.message,
        action: {
          label: 'Retry',
          onClick: () => {
            void refetch()
          },
        },
      })
    }
  }, [error, refetch])

  if (isPending && !data) {
    return <BillingFamilyBalanceSkeleton />
  }

  if (!data?.me) {
    return (
      <NoHouseholdState
        onAdded={() => {
          void refetch()
        }}
      />
    )
  }

  // Owned household first; households the viewer co-owns render below. Each
  // one shows the single household balance (PROD-151) — what differs is which
  // actions sit beside it, which comes from the viewer's role: TOP_UP gates
  // add-funds, MANAGE_PAYMENT_METHODS gates the cards section. A guardian sees
  // the balance and history only.
  const household = findActiveHousehold(data.me.ownedAccounts.filter((a) => a.isOwner))
  const sharedHouseholds = data.me.ownedAccounts.filter((a) => a.kind === 'HOUSEHOLD' && !a.isArchived && !a.isOwner)
  if (!household && sharedHouseholds.length === 0) {
    return (
      <NoHouseholdState
        onAdded={() => {
          void refetch()
        }}
      />
    )
  }

  return (
    <div className="flex flex-col gap-6">
      {returnTo ? <ReturnToOrderBar returnTo={returnTo} landed={topUpLanded} /> : null}
      {household && (
        <>
          <BalanceCard
            account={household}
            pending={topUp.pendingAccountId === household.id}
            stillProcessing={topUp.stillProcessing}
            onTopUp={() => setTopUpOpen(true)}
            onRefetch={() => {
              void refetch()
            }}
          />

          <PaymentMethodsCard
            account={household}
            openAddOnMount={openAddPaymentMethod}
            onAddOpened={() =>
              // Opened once: a refresh or back-navigation must not reopen it.
              setSearchParams(
                (params) => {
                  params.delete(ADD_PAYMENT_METHOD_PARAM)
                  return params
                },
                { replace: true },
              )
            }
          />

          <LedgerTable account={household} />

          {household.fundingEnabled !== false ? (
            <TopUpSheet
              open={topUpOpen}
              onOpenChange={setTopUpOpen}
              account={household}
              onPaid={(args) => {
                setWatchStarted(true)
                topUp.watch(args)
              }}
            />
          ) : null}
        </>
      )}

      {sharedHouseholds.map((shared) => (
        <div key={shared.id} className="flex flex-col gap-6">
          <BalanceCard
            account={shared}
            sharedMode
            readOnly={!shared.permissions.includes('TOP_UP')}
            onTopUp={() => setSharedTopUpAccountId(shared.id)}
            onRefetch={() => {
              void refetch()
            }}
          />
          {shared.permissions.includes('MANAGE_PAYMENT_METHODS') ? <PaymentMethodsCard account={shared} /> : null}
          <LedgerTable account={shared} />
          {shared.fundingEnabled !== false && shared.permissions.includes('TOP_UP') ? (
            <TopUpSheet
              open={sharedTopUpAccountId === shared.id}
              onOpenChange={(next) => setSharedTopUpAccountId(next ? shared.id : null)}
              account={shared}
              onPaid={(args) => {
                setWatchStarted(true)
                topUp.watch(args)
              }}
            />
          ) : null}
        </div>
      ))}
    </div>
  )
}
